// Core hook wiring for TaskItemBase controller.
import { useNavigate, useLocation } from 'react-router-dom';

import {
  useBreakpoint,
  breakpointOrder,
} from '@/hooks/ui/useBreakpoint.js';
import useTaskRollup from '@/features/tasks/hooks/status/use-task-rollup.js';
import { useTaskItemProps } from '@/features/tasks/hooks/ui/use-task-item-props.js';
import { useTaskUI } from '@/features/tasks/context/TaskUIContext';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import { createTaskItemDoubleClickHandler } from './useTaskItemBaseController.helpers';
import { useTaskItemBaseInteraction } from './useTaskItemBaseController.interaction';
import { useTaskItemBasePresentation } from './useTaskItemBaseController.presentation';

export function useTaskItemBaseSetup(props) {
  const {
    selectedTaskIds = [],
    isMultiSelectActive = false,
    onToggleInSelection = () => {},
    dragEnabled = false,
    listId = null,
    focusOwnerId = null,
    addSubtaskOnParentsOnly = false,
    menuSurface = null,
    dragType = null,
    disableRowNavigate = false,
  } = useTaskUI();
  const { tasksMap } = useTasksContext();
  const fullProps = useTaskItemProps(props);
  const {
    task,
    isOverlay,
    variant,
    breadcrumb,
    index,
    showPlayButton,
    user: finalUser,
    tasks: finalTasks,
    projectDisplayMap: finalProjectDisplayMap,
    projectColorMap: finalProjectColorMap,
    contextDisplayMap: finalContextDisplayMap,
    activeFocusTaskId: finalActiveFocusTaskId,
    focusStartTime: finalFocusStartTime,
    stopFocus: finalStopFocus,
    handleStartFocus: finalHandleStartFocus,
    handleToggleComplete: finalHandleToggleComplete,
    handleDeleteTask: finalHandleDeleteTask,
    view,
    indentationType,
    onStartAddingSubtask,
    onRebalance,
    duplicateOrderKeyTaskIds,
    isExecutionView,
    breadcrumbArray,
    displayLevel,
  } = fullProps;

  const navigate = useNavigate();
  const location = useLocation();
  const currentBreakpoint = useBreakpoint();
  const isSmallScreen = breakpointOrder[currentBreakpoint] <= breakpointOrder.s;
  const { displayDuration, isRollup } = useTaskRollup(task);

  const {
    localCompleted,
    setLocalCompleted,
    isDragDisabled,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
    isTouchDevice,
    mergedEventHandlers,
    showRowDelete,
    menuState,
    closeMenu,
  } = useTaskItemBaseInteraction({
    task,
    isRollup,
    view,
    dragEnabled,
    listId,
    dragType,
    indentationType,
    variant,
    menuSurface,
    tasksMap,
  });

  const { classNames, finalStyle, breadcrumbTooltip, contentProps } =
    useTaskItemBasePresentation({
      task,
      isOverlay,
      variant,
      breadcrumb,
      index,
      showPlayButton,
      finalUser,
      finalTasks,
      finalProjectDisplayMap,
      finalProjectColorMap,
      finalContextDisplayMap,
      finalActiveFocusTaskId,
      finalFocusStartTime,
      finalStopFocus,
      finalHandleStartFocus,
      finalHandleToggleComplete,
      finalHandleDeleteTask,
      view,
      indentationType,
      onStartAddingSubtask,
      onRebalance,
      duplicateOrderKeyTaskIds,
      isExecutionView,
      breadcrumbArray,
      displayLevel,
      menuSurface,
      addSubtaskOnParentsOnly,
      isSmallScreen,
      listId,
      focusOwnerId,
      selectedTaskIds,
      isMultiSelectActive,
      onToggleInSelection,
      localCompleted,
      setLocalCompleted,
      isDragDisabled,
      transform,
      transition,
      isDragging,
      isOver,
      isTouchDevice,
      showRowDelete,
      displayDuration,
      isRollup,
      tasksMap,
    });

  const handleDoubleClick = createTaskItemDoubleClickHandler(
    navigate,
    location,
    task,
    { disableRowNavigate },
  );

  return {
    task,
    variant,
    isDragDisabled,
    setNodeRef,
    finalStyle,
    classNames,
    eventHandlers: mergedEventHandlers,
    displayLevel,
    breadcrumbTooltip,
    contentProps,
    handleDoubleClick,
    menuState,
    closeMenu,
    menuSurface,
    onStartAddingSubtask,
    index,
    tasks: finalTasks,
    isExecutionView,
  };
}
